
import { useState, useEffect } from "react";
import { SakuraPetals } from "@/components/SakuraPetals";
import { CategorySidebar } from "@/components/CategorySidebar";
import { HabitList } from "@/components/HabitList";
import { useSupabaseHabits } from "@/hooks/useSupabaseHabits";
import { useSupabaseCategories } from "@/hooks/useSupabaseCategories";
import { useIsMobile } from "@/hooks/use-mobile";
import { AddHabitSection } from "@/components/habit/AddHabitSection";
import { FilterButtons } from "@/components/habit/FilterButtons";
import { useCategoryManagement } from "@/hooks/useCategoryManagement";
import { useSyncLocalData } from "@/hooks/useSyncLocalData";
import { OnboardingTrigger } from "@/components/onboarding/OnboardingTrigger";

const Index = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(() => {
    const savedCategory = localStorage.getItem('selectedCategory');
    return savedCategory ? JSON.parse(savedCategory) : null;
  });
  
  const [showCompleted, setShowCompleted] = useState(false);
  const isMobile = useIsMobile();
  
  // Replace useHabits with useSupabaseHabits
  const {
    habits,
    loading: habitsLoading,
    handleAddHabit,
    handleComplete,
    handleRevert,
    handleDelete,
    handleUpdateCategories,
    handleDuplicate,
    handleUpdatePriority,
    handleUpdateTitle,
    handleUpdateDailyTarget,
  } = useSupabaseHabits();
  
  // Replace useCategories with useSupabaseCategories
  const {
    categories,
    loading: categoriesLoading,
    handleRemoveCategory,
    handleAddNewCategoryToSystem,
    handleUpdateCategory,
    getAllCategories,
  } = useSupabaseCategories();
  
  // Trigger data sync from local storage to Supabase
  useSyncLocalData();
  
  // Define functions that were missing
  const handleCategoryDeleted = (categoryName: string) => {
    console.log(`Category "${categoryName}" was deleted`);
    // This is handled by Supabase now
  };
  
  const handleCategoryRenamed = (oldName: string, newName: string) => {
    console.log(`Category renamed from "${oldName}" to "${newName}"`);
    // This is handled by Supabase now
  };
  
  const {
    handleCategoryRemove,
    handleCategoryUpdate,
    handleAddCategory,
  } = useCategoryManagement({
    handleRemoveCategory,
    handleUpdateCategory,
    handleAddNewCategoryToSystem,
    handleCategoryDeleted,
    handleCategoryRenamed,
    setSelectedCategory,
    selectedCategory,
  });

  useEffect(() => {
    localStorage.setItem('selectedCategory', JSON.stringify(selectedCategory));
  }, [selectedCategory]);

  useEffect(() => {
    const handleCategorySelection = (event: Event) => {
      const customEvent = event as CustomEvent;
      setSelectedCategory(customEvent.detail.category);
    };

    window.addEventListener('category-selected', handleCategorySelection);

    return () => {
      window.removeEventListener('category-selected', handleCategorySelection);
    };
  }, []);

  const handleUpdateDailyTargetWrapper = (
    id: string, 
    dailyTarget: { value: number; unit: string } | undefined
  ) => {
    if (dailyTarget) {
      handleUpdateDailyTarget(id, dailyTarget.value, dailyTarget.unit);
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-background">
      <OnboardingTrigger />
      <CategorySidebar
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        handleRemoveCategory={handleCategoryRemove}
        handleAddNewCategoryToSystem={handleAddCategory}
        handleUpdateCategory={handleCategoryUpdate}
        categories={categories}
      />

      <main className="flex-1 pt-3 overflow-x-hidden relative w-full">
        <SakuraPetals />
        <div className="container mx-auto space-y-6 px-0 max-w-[1200px]">
          {selectedCategory && (
            <p className="text-primary font-semibold px-4 sm:px-6">
              Viewing: {selectedCategory}
            </p>
          )}

          <div className="w-full px-4 sm:px-6 flex flex-col space-y-4">
            <AddHabitSection 
              habits={habits} 
              onAddHabit={handleAddHabit} 
            />

            <FilterButtons 
              showCompleted={showCompleted} 
              setShowCompleted={setShowCompleted} 
            />
          </div>

          <HabitList
            habits={habits}
            selectedCategory={selectedCategory}
            handleComplete={handleComplete}
            handleRevert={handleRevert}
            handleDelete={handleDelete}
            handleUpdateCategories={handleUpdateCategories}
            handleUpdatePriority={handleUpdatePriority}
            handleDuplicate={handleDuplicate}
            handleUpdateTitle={handleUpdateTitle}
            handleUpdateDailyTarget={handleUpdateDailyTargetWrapper}
            getAllCategories={() => categories}
            handleAddNewCategoryToSystem={handleAddCategory}
            showCompleted={showCompleted}
          />
        </div>
      </main>
    </div>
  );
};

export default Index;
